import { z } from "zod";

/**
 * Admin Dashboard overview (US-AE.F9 dead-feature fix · @UC9099) — READ-ONLY aggregate cho màn
 * Tổng quan (route `/`). Thay fake data đóng cứng bằng số liệu THẬT: KPI đếm nhóm/user active,
 * queue counts (disputes open / risk pending / privacy pending / abuse open / correction pending /
 * drift) + recent audit.
 *
 * Tiền: KHÔNG tính ở client (TDD §2). AUM chưa có nguồn aggregate an toàn → để null (FE hiện "—").
 * Redaction: các đếm là aggregate không-PII → mọi 4 vai đọc được. Recent-activity dùng audit metadata.
 */

/** Đếm 1 hàng đợi cần xử lý (feed KPI queue + badge Topbar/Sidebar). */
export const adminQueueSummarySchema = z.object({
  /** Tranh chấp đang mở (Dispute.status='open'). */
  disputes: z.number().int().nonnegative(),
  /** Mục rủi ro chờ duyệt (RiskReviewItem.status='pending'). */
  risk: z.number().int().nonnegative(),
  /** Yêu cầu PII chờ xử lý (PrivacyRequest.status='pending'). */
  gdpr: z.number().int().nonnegative(),
  /** Account lệch số dư đối soát (drift). null nếu chưa có nguồn drift. */
  drift: z.number().int().nonnegative(),
  /** Báo cáo lạm dụng chờ kiểm duyệt (AbuseReport.status='open'). */
  abuse: z.number().int().nonnegative(),
  /** Điều chỉnh ledger chờ duyệt (correction pending). */
  corrections: z.number().int().nonnegative(),
  /** Yêu cầu hỗ trợ gửi từ app chưa xử lý (support_tickets source='in_app', status open|pending — @UC9105). */
  supportRequests: z.number().int().nonnegative(),
});
export type AdminQueueSummary = z.infer<typeof adminQueueSummarySchema>;

/** KPI tổng quan (đếm thật; giá trị tiền để null nếu chưa có aggregate an toàn). */
export const adminOverviewKpiSchema = z.object({
  activeGroups: z.number().int().nonnegative(),
  totalUsers: z.number().int().nonnegative(),
  activeFunds: z.number().int().nonnegative(),
  /** AUM (minor units, string BigInt) — null khi chưa có nguồn aggregate an toàn. */
  aumMinor: z.string().nullable(),
});
export type AdminOverviewKpi = z.infer<typeof adminOverviewKpiSchema>;

/**
 * 1 dòng hoạt động admin gần đây (từ audit_log). Chỉ action/entity/thời gian + actor display-name —
 * KHÔNG lộ PII thô.
 *
 * T04 (US-AE-IMP2 · @UC9099): thêm actorUserId (uuid, nullable — audit hệ thống không có actor) +
 * actorName (display-name resolve qua NameResolverService T01, redact theo vai; null khi actor mồ côi
 * → FE fallback). Additive — action/entity/at giữ nguyên.
 */
export const adminRecentActivitySchema = z.object({
  id: z.string(),
  /** Actor uuid (không PII). null = audit hệ thống / không có actor. */
  actorUserId: z.string().nullable(),
  /** Display-name actor (username/email-mask/phone-mask theo vai). null = không resolve được. */
  actorName: z.string().nullable(),
  action: z.string(),
  entity: z.string().nullable(),
  at: z.string(), // ISO8601
});
export type AdminRecentActivity = z.infer<typeof adminRecentActivitySchema>;

/** Envelope dashboard overview. */
export const adminDashboardResponseSchema = z.object({
  kpi: adminOverviewKpiSchema,
  queues: adminQueueSummarySchema,
  recentActivity: z.array(adminRecentActivitySchema),
});
export type AdminDashboardResponse = z.infer<typeof adminDashboardResponseSchema>;
